/**
 * StayOn HTTP-Client — ky-Wrapper mit Auto-Refresh-Pipeline (Cookie-Session).
 *
 * Architektur:
 *   1. `credentials: 'include'` auf JEDEM Request → das httpOnly-Refresh-Cookie fließt mit
 *   2. Vor JEDEM Request: Access-Token aus Store anhängen (sofern vorhanden)
 *   3. Nach 401-Response: Single-Flight-Refresh starten (POST /refresh, Cookie + CSRF-Header)
 *      → Refresh erfolgreich: Original-Request mit neuem Token wiederholen
 *      → definitiver Auth-Verlust: Store leeren; temporärer Fehler: Sitzung erhalten
 *
 * Der Refresh-Token liegt NICHT in JS — er ist das Cookie; Refresh braucht nur den in-memory
 * CSRF-Token (Double-Submit). Den Kaltstart aus dem Cookie macht `hydrate()` im Auth-Store.
 *
 * Während einer Support-Sitzung tragen Requests das Support-Token. Es hat kein Cookie: ein
 * abgelaufenes oder abgelehntes stellt die Sitzung über die Heimatsitzung neu aus. Eine an eine
 * Identität gebundene Api schickt nur mit deren Token (`requestIdentity.ts`).
 */
import ky, { type KyInstance } from 'ky';
import { isExpired, type TokenStore } from '@stayon/auth-core';
import {
  createRefreshCoordinator,
  type RefreshCoordinator,
} from '@/shared/auth/refreshCoordinator';
import { createCookieRefreshFlight, type RefreshState } from './cookieRefreshFlight';
import { assertIdentity } from './requestIdentity';
import { skipsRefresh } from './refreshExemptions';
import {
  bearerOf,
  SupportSessionEndedError,
  supportSessionOf,
  type SupportTokenSource,
} from './supportBearer';

export type { RefreshState } from './cookieRefreshFlight';

/** Kontext für Requests, die immer mit der Heimatsitzung laufen, auch während einer Support-Sitzung. */
export const HOME_SESSION_CONTEXT = { session: 'home' };

export interface HttpClientOptions {
  baseUrl: string;
  tokenStore: TokenStore;
  /** Wird beim definitiven Auth-Verlust gerufen — typisch: Router auf /login schicken. */
  onUnauthenticated?: () => void;
  onRefreshState?: (state: RefreshState) => void;
  refreshCoordinator?: RefreshCoordinator;
  /** Token der Support-Sitzung; solange keine läuft, gilt überall die Heimatsitzung. */
  support?: SupportTokenSource;
}

function retryWith(request: Request, token: string): Promise<Response> {
  // `set` ersetzt den alten Bearer. Ein Objekt aus `headers.entries()` trüge ihn klein geschrieben
  // weiter, und neben `Authorization` ergäbe das „Bearer alt, Bearer neu“ — der Server lehnt beides ab.
  const headers = new Headers(request.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return ky(request.url, {
    method: request.method,
    headers,
    body: request.body as BodyInit | null,
    credentials: request.credentials,
    retry: 0,
  });
}

export function createHttpClient({
  baseUrl,
  tokenStore,
  onUnauthenticated,
  onRefreshState,
  refreshCoordinator = createRefreshCoordinator({ tokenStore }),
  support,
}: HttpClientOptions): KyInstance {
  const singleFlightRefresh = createCookieRefreshFlight({
    baseUrl,
    tokenStore,
    refreshCoordinator,
    onUnauthenticated,
    onRefreshState,
  });

  /** Bearer für den Ziel-Workspace; `null`, solange der Tab in seiner Heimatsitzung arbeitet. */
  async function supportBearer(): Promise<string | null> {
    const token = support?.current() ?? null;
    if (!support || token === null || !isExpired(token)) return token;
    const renewed = await support.renew(token, false);
    if (!renewed) throw new SupportSessionEndedError();
    return renewed;
  }

  return ky.create({
    prefixUrl: baseUrl,
    timeout: 30_000,
    retry: { limit: 0 }, // wir bauen Retry-on-401 selbst, kein blindes Backoff
    credentials: 'include', // httpOnly-Refresh-Cookie muss cross-origin mitfließen
    hooks: {
      beforeRequest: [
        async (request, options) => {
          const supportToken = options.context.session === 'home' ? null : await supportBearer();
          if (supportToken) {
            assertIdentity(options.context, supportToken);
            request.headers.set('Authorization', `Bearer ${supportToken}`);
            return;
          }
          // Vor einem Refresh prüfen: eine fremde Identität braucht keine erneuerte Anmeldung.
          assertIdentity(options.context, tokenStore.get()?.accessToken);
          let session = tokenStore.get();
          const isLogout = request.url.endsWith('/api/v1/auth/logout');
          // Access-Token abgelaufen (clock-skew 30 s) UND wir haben eine Session (CSRF-Token)
          // → proaktiv refreshen statt auf 401 zu warten.
          if (
            !isLogout &&
            session?.csrfToken &&
            (!session.accessToken || isExpired(session.accessToken))
          ) {
            session = await singleFlightRefresh();
            assertIdentity(options.context, session?.accessToken);
          }
          if (session?.accessToken) {
            request.headers.set('Authorization', `Bearer ${session.accessToken}`);
          }
          // /logout liest den Refresh-Token aus dem Cookie und braucht den Double-Submit-
          // CSRF-Header. Ihn hier aus der LIVE-Session setzen (nach etwaigem Proaktiv-Refresh),
          // damit er zum rotierten stayon_csrf-Cookie passt — nicht vorab in endpoints.ts fixen.
          if (session?.csrfToken && isLogout) {
            request.headers.set('X-CSRF-Token', session.csrfToken);
          }
        },
      ],
      afterResponse: [
        async (request, options, response) => {
          const path = new URL(request.url).pathname;
          if (response.status !== 401 || (await skipsRefresh(path, response))) return response;
          const sent = bearerOf(request);
          const renewed =
            sent && supportSessionOf(sent)
              ? ((await support?.renew(sent, true)) ?? null)
              : ((await singleFlightRefresh())?.accessToken ?? null);
          // Ohne neues Token bleibt der 401 stehen, der Aufrufer behandelt ihn; sonst genau einmal
          // wiederholen — und nur, wenn das neue Token noch zur Identität des Requests gehört.
          if (!renewed) return response;
          assertIdentity(options.context, renewed);
          return retryWith(request, renewed);
        },
      ],
    },
  });
}
